import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {isoDate, parseIsoDate} from '@/entities/attendance/period';
import {invalidateSalaryDependent} from '@/entities/attendance/cache';
import {useSalaryAccess} from '@/entities/permission/access';
import {deleteSalary, fetchSalaryHistory} from '@/entities/salary/api';
import {formatMoney} from '@/entities/salary/money';
import type {SalaryRecord} from '@/entities/salary/types';
import {ApiError} from '@/shared/api';
import {formatDate} from '@/shared/lib/format';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton, IconButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {SectionHeader} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {SalaryForm} from './SalaryForm';

/**
 * «Оплата» в карточке сотрудника — история объявленных ставок.
 *
 * ## Почему это отдельный запрос, а не поле карточки
 *
 * `GET /control/personnel/{id}` оклад не отдаёт и не будет: карточку читает всякий с
 * правом `personnel/read`, включая оператора на проходной. Отсюда и второй запрос, и
 * `useSalaryAccess` над ним — раздел не рисуется вовсе, пока `salaries/read` не
 * подтверждено. Это осознанное исключение из общего правила кабинета «не прятать, а
 * узнавать из 403»: пустая колонка «Оклад» с прочерками всё равно сообщила бы
 * оператору, что ведомость существует и где она.
 *
 * ## Почему история, а не одно число
 *
 * Оклад меняется, и меняется С ЧИСЛА. Одно поле помнит только последнее значение — и
 * отчёт за август посчитался бы по сентябрьской ставке, молча и правдоподобно. Запись
 * действует от своей `effective_from` до `effective_from` следующей; конца у записи нет,
 * иначе две строки могли бы разойтись в том, кому принадлежит один и тот же день.
 *
 * ## Почему нет «карандаша»
 *
 * Правки записи в API нет намеренно. Ошибочную удаляют и заводят заново; повышение —
 * это НОВАЯ запись, а старую трогать нельзя, она держит верным отчёт за прошлый месяц.
 * Поэтому у строки есть «удалить» и нет «изменить», и это не недоделка формы.
 *
 * Будущие ставки в списке видны, но текущей не считаются: «действует сейчас» — это
 * `current`, то есть запись, действующая СЕГОДНЯ.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(2)},
  current: {gap: spacing(1)},
  currentRow: {flexDirection: 'row', alignItems: 'flex-end', gap: spacing(2)},
  history: {gap: spacing(2)},
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  rowText: {flex: 1, gap: 2},
  future: {
    alignSelf: 'flex-start',
    paddingHorizontal: spacing(2),
    paddingVertical: 2,
    borderRadius: radii.sm,
  },
  actions: {gap: spacing(2)},
});

/** «01.09.2026» из «2026-09-01» — тот же порядок, что во всём приложении. */
const showDate = (iso: string): string => formatDate(parseIsoDate(iso).getTime());

/** Действует ли запись только с будущей даты. */
function isFuture(record: SalaryRecord, today: string): boolean {
  return record.effective_from > today;
}

function SalaryRow({
  record,
  today,
  canDelete,
  onDelete,
  busy,
}: {
  record: SalaryRecord;
  today: string;
  /** Оружие снимается со строки, которая уже взведена: подтверждение ниже — её. */
  canDelete: boolean;
  onDelete: () => void;
  busy: boolean;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const future = isFuture(record, today);

  return (
    <View style={styles.row} testID={`salary-row-${record.id}`}>
      <View style={styles.rowText}>
        <AppText variant="mono">
          {formatMoney(record.amount_minor, record.currency)}
        </AppText>
        <AppText variant="caption">
          {t('salary.effectiveFromValue', {
            date: showDate(record.effective_from),
            kind: t(`salary.kindShort.${record.kind}`, record.kind),
          })}
        </AppText>
        {record.note ? (
          <AppText variant="caption" numberOfLines={2}>
            {record.note}
          </AppText>
        ) : null}
        {future ? (
          <View style={[styles.future, {backgroundColor: p.muted}]}>
            <AppText variant="micro">{t('salary.future')}</AppText>
          </View>
        ) : null}
      </View>
      {canDelete ? (
        <IconButton
          icon="trash"
          tone="ghost"
          label={t('actions.delete')}
          disabled={busy}
          onPress={onDelete}
          testID={`salary-delete-${record.id}`}
        />
      ) : null}
    </View>
  );
}

export function SalarySection({
  companyId,
  recordId,
}: {
  companyId: string;
  recordId: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  const access = useSalaryAccess(companyId);

  const [adding, setAdding] = useState(false);
  /** Удаление в два касания: запись истории не восстановить, её можно только завести заново. */
  const [armed, setArmed] = useState<string | null>(null);

  const history = useQuery({
    queryKey: ['salary', 'history', companyId, recordId],
    queryFn: () => fetchSalaryHistory(companyId, recordId),
    enabled: access.canRead,
  });

  const remove = useMutation({
    mutationFn: (salaryId: string) => deleteSalary(companyId, recordId, salaryId),
    onSuccess: async () => {
      setArmed(null);
      await invalidateSalaryDependent(qc);
    },
  });

  // Ни раздела, ни «доступа нет»: пока права выясняются, здесь не должно быть ничего —
  // а после ответа раздел либо есть, либо его не существует для этого человека.
  if (access.isPending || !access.canRead) {
    return null;
  }

  // Через `isoDate`, а не через toISOString(): тот переводит в UTC и до 05:00 по
  // Ташкенту отдаёт вчера — то есть ставка, начинающая действовать сегодня, полночи
  // выглядела бы будущей.
  const todayIso = isoDate(new Date());
  const records = history.data?.records ?? [];
  const current = history.data?.current ?? null;

  /**
   * `403` тут всё-таки возможен, и это не сбой: право читается из `/permissions/me`, а
   * проверяет запрос гейтвей — между ними лежит и рассинхрон кэша, и отзыв права
   * посреди сессии.
   */
  const forbidden = history.error instanceof ApiError && history.error.status === 403;

  let body: React.ReactNode;

  if (history.isPending) {
    body = <Skeleton height={96} radius={radii.lg} />;
  } else if (forbidden) {
    body = <Banner tone="info" title={t('salary.forbidden')} />;
  } else if (history.isError) {
    body = (
      <EmptyState
        tone="error"
        title={t('salary.error')}
        actionLabel={t('actions.retry')}
        onAction={() => void history.refetch()}
      />
    );
  } else {
    body = (
      <Card padding={4} style={styles.history} testID="salary-card">
        <View style={styles.current}>
          <AppText variant="eyebrow">{t('salary.current')}</AppText>
          <View style={styles.currentRow}>
            <AppText
              variant="monoLg"
              color={current ? p.foreground : p.mutedForeground}
              testID="salary-current">
              {current
                ? formatMoney(current.amount_minor, current.currency)
                : t('salary.none')}
            </AppText>
            {current ? (
              <AppText variant="caption">
                {t(`salary.kindShort.${current.kind}`, current.kind)}
              </AppText>
            ) : null}
          </View>
          {/* Пусто — это не ноль: до первой записи ставки нет вовсе, и ведомость
              такого человека пропустит с флагом «оклад не задан». */}
          {!current ? (
            <AppText variant="caption">{t('salary.noneHint')}</AppText>
          ) : null}
        </View>

        {records.length > 0 ? (
          <View style={styles.history}>
            <AppText variant="eyebrow">{t('salary.history')}</AppText>
            {records.map(record => (
              <SalaryRow
                key={record.id}
                record={record}
                today={todayIso}
                canDelete={access.canDelete && armed !== record.id}
                onDelete={() => setArmed(record.id)}
                busy={remove.isPending}
              />
            ))}
          </View>
        ) : null}

        {/* Взведённое удаление — отдельной кнопкой во всю ширину, а не второй иконкой
            рядом с первой: у строки истории нет отмены, и подтверждение должно быть
            непохоже на то, что его вызвало. */}
        {armed ? (
          <View style={styles.actions}>
            <AppButton
              title={t('salary.confirmDelete')}
              tone="destructive"
              icon="trash"
              loading={remove.isPending}
              onPress={() => remove.mutate(armed)}
              testID="salary-delete-confirm"
            />
            <AppButton
              title={t('actions.cancel')}
              tone="ghost"
              onPress={() => setArmed(null)}
              disabled={remove.isPending}
            />
          </View>
        ) : null}

        {remove.isError ? (
          <Banner tone="error" title={messageOf(remove.error, t('errors.unknown'))} />
        ) : null}

        {access.canCreate ? (
          <AppButton
            title={t('salary.add')}
            tone="soft"
            icon="plus"
            onPress={() => setAdding(true)}
            testID="salary-add"
          />
        ) : null}
      </Card>
    );
  }

  return (
    <View style={styles.root} testID="salary-section">
      <SectionHeader title={t('salary.title')} hint={t('salary.hint')} />
      {body}

      {/* key переключается на открытии, чтобы форма пересеивалась, а не помнила
          брошенный черновик прошлой ставки. */}
      <SalaryForm
        key={adding ? `salary-add-${recordId}` : 'salary-add-none'}
        companyId={companyId}
        recordId={recordId}
        visible={adding}
        onClose={() => setAdding(false)}
      />
    </View>
  );
}
